import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  styled,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import { AppDispatch, RootState } from "../../stores";
import { updateUserById } from "../../features/user-management-module/userAction";
import { User } from "../../features/user-management-module/userAction";
import { ArrowBack } from "@mui/icons-material";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "90vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.95)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "85vh",
  },
}));

const FormSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "component" && prop !== "onSubmit",
})<{ component?: React.ElementType; onSubmit?: (e: React.FormEvent) => void }>(
  ({ theme }) => ({
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: "8px",
    backgroundColor: "#f8f8f8",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    "&:hover": {
      transition: "background-color 0.2s ease-in-out",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      margin: theme.spacing(0, 1),
    },
  })
);

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3f51b5",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  color: "#f8f9fa",
  borderRadius: "8px",
  padding: theme.spacing(1, 2.5),
  fontSize: "0.9rem",
  "&:hover": {
    backgroundColor: "#354142",
  },
  "&:disabled": {
    background: "#cccccc",
    color: "#666666",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8, 2),
    fontSize: "0.85rem",
  },
}));

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
}));

const EditUser: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { users, loading: usersLoading } = useSelector(
    (state: RootState) => state.users
  );
  const selectedUser = users.find((user: User) => user.id === userId) || null;

  const [editFormData, setEditFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
  });

  useEffect(() => {
    if (!selectedUser && userId) {
      navigate("/main/user-management");
    } else if (selectedUser) {
      setEditFormData({
        firstname: selectedUser.firstname,
        lastname: selectedUser.lastname,
        email: selectedUser.email,
      });
    }
  }, [selectedUser, userId, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = () => {
    if (selectedUser) {
      dispatch(
        updateUserById({
          id: selectedUser.id,
          data: editFormData,
        })
      ).then(() => {
        navigate("/main/user-management");
      });
    }
  };

  // Animation variants for content
  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  const buttonVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          maxWidth: "auto",
          margin: "0 auto",
          paddingBottom: "80px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600} color="#4b5b5d">
            Edit User
          </Typography>
          <Tooltip title="Back">
            <StyledBackButton
              aria-label="back"
              onClick={() => navigate("/main/user-management")}
            >
              <ArrowBack />
            </StyledBackButton>
          </Tooltip>
        </Box>

        {usersLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress
              size={25}
              thickness={2}
              sx={{ color: "#455354" }}
            />
          </Box>
        )}
        {!usersLoading && selectedUser ? (
          <FormSection component="form" onSubmit={handleEditSubmit}>
            <motion.div
              variants={contentVariants}
              initial="hidden"
              animate="visible"
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <motion.div variants={itemVariants}>
                    <StyledTextField
                      name="firstname"
                      label="First Name"
                      variant="outlined"
                      value={editFormData.firstname}
                      onChange={handleChange}
                      required
                    />
                  </motion.div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <motion.div variants={itemVariants}>
                    <StyledTextField
                      name="lastname"
                      label="Last Name"
                      variant="outlined"
                      value={editFormData.lastname}
                      onChange={handleChange}
                      required
                    />
                  </motion.div>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <motion.div variants={itemVariants}>
                    <StyledTextField
                      name="email"
                      label="Email"
                      variant="outlined"
                      value={editFormData.email}
                      onChange={handleChange}
                      type="email"
                      required
                    />
                  </motion.div>
                </Grid>
              </Grid>
            </motion.div>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 2,
              }}
            >
              <motion.div
                variants={buttonVariants}
                initial="rest"
                whileHover="hover"
                whileTap="tap"
              >
                <AddButton
                  type="submit"
                  disabled={usersLoading}
                  onClick={handleEditSubmit}
                >
                  {usersLoading ? (
                    <CircularProgress size={20} sx={{ color: "#f8f9fa" }} />
                  ) : (
                    "Update User"
                  )}
                </AddButton>
              </motion.div>
              <motion.div
                variants={buttonVariants}
                initial="rest"
                whileHover="hover"
                whileTap="tap"
              >
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    borderColor: "#4b5b5d",
                    color: "#4b5b5d",
                    borderRadius: "8px",
                    fontWeight: 600,
                    px: 2.5,
                    py: 0.8,
                    "&:hover": {
                      backgroundColor: "rgba(75, 91, 93, 0.1)",
                      borderColor: "#4b5b5d",
                    },
                    "&:disabled": {
                      borderColor: "#cccccc",
                      color: "#666666",
                    },
                  }}
                  onClick={() => navigate("/main/user-management")}
                  disabled={usersLoading}
                >
                  Cancel
                </Button>
              </motion.div>
            </Box>
          </FormSection>
        ) : (
          !usersLoading && (
            <Typography variant="h6" color="#4b5b5d" textAlign="center">
              User not found
            </Typography>
          )
        )}
      </motion.div>
    </Container>
  );
};

export default EditUser;
